import { type Datacenter, DatacenterStatus } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import { type Ref, ref } from 'vue';

/**
 * Sorts datacenters by region and latency
 *
 * Sorts datacenters by region and latency.
 * Datacenters in the same region are sorted by latency.
 * Unavailable datacenters are put at the end.
 * If all datacenters in the region are unavailable, the region is sorted by country code.
 *
 * Example:
 * FRA (DEU): 10ms
 * DUS (DEU): 120ms <- slower than LON, but in the same region as FRA
 * LON (GBR): 20ms
 * NYC (USA): 30ms
 * SFO (USA): 40ms
 * DFW (USA): UNAVAILABLE <- put at the end at the same region
 * SYD (AUS): 50ms
 * HND (JPN): COMING_SOON <- put at the end sorted by country code and name
 * KIX (JPN): COMING_SOON <- put at the end sorted by country code and name
 * SIN (SGP): COMING_SOON <- put at the end sorted by country code
 *
 * @param datacenters - list of datacenters to sort
 * @param latestLatency - latest latency data
 */
export default (
  datacenters: Ref<Array<Datacenter>>,
  latestLatency: Ref<{ [key: string]: number | null }>,
) => {
  const sortedDatacenters = ref<Array<Datacenter>>([]);
  const sortDatacenters = () => {
    // prepare datacenters grouped by region (countryCode)
    const grouped = datacenters.value.reduce<
      Array<{ countryCode: string; datacenters: Array<Datacenter> }>
    >((acc, dc) => {
      const group = acc.find((g) => g.countryCode === dc.region.countryCode);

      if (group == null) {
        acc.push({
          countryCode: dc.region.countryCode,
          datacenters: [dc],
        });
      } else {
        group.datacenters.push(dc);
      }

      return acc;
    }, []);

    // now sort the datacenters by latency in their respective region
    grouped.forEach((group) => {
      group.datacenters.sort((a, b) => {
        // in case datacenter not available, put it at the end
        if (a.status !== DatacenterStatus.AVAILABLE) {
          // in case the other datacenter is also not available, sort by datacenter name
          if (b.status !== DatacenterStatus.AVAILABLE) {
            return a.name.localeCompare(b.name);
          }

          // otherwise, put it at the end
          return 1;
        }

        // in case there is no latency data, put it at the end
        if (latestLatency.value[a.latencyEndpoint] == null) {
          return 1;
        }

        if (latestLatency.value[b.latencyEndpoint] == null) {
          return -1;
        }

        return latestLatency.value[a.latencyEndpoint] - latestLatency.value[b.latencyEndpoint];
      });
    });

    // now sort groups by the fastest datacenter
    grouped.sort((a, b) => {
      // in case of all datacenters in region are not available
      if (a.datacenters.every((dc) => dc.status !== DatacenterStatus.AVAILABLE)) {
        // if the other region has at least one available datacenters, put it at the top
        if (b.datacenters.some((dc) => dc.status === DatacenterStatus.AVAILABLE)) {
          return 1;
        }

        // otherwise, sort by country code
        return a.countryCode.localeCompare(b.countryCode);
      }

      if (latestLatency.value[a.datacenters[0].latencyEndpoint] == null) {
        return 1;
      }

      if (latestLatency.value[b.datacenters[0].latencyEndpoint] == null) {
        return -1;
      }

      // since datacenters in their region are sorted by latency, we can just compare the first datacenter
      return (
        latestLatency.value[a.datacenters[0].latencyEndpoint] -
        latestLatency.value[b.datacenters[0].latencyEndpoint]
      );
    });

    // now flatten the object to array
    sortedDatacenters.value = grouped.reduce<Array<Datacenter>>((acc, group) => {
      acc.push(...group.datacenters);
      return acc;
    }, []);
  };

  return {
    sortedDatacenters,
    sortDatacenters,
  };
};
